import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Seo } from "../lib/seo";
import { MaskText, Reveal, ParallaxImage, EASE } from "../lib/motion";
import { QuoteForm } from "../components/QuoteForm";
import { IMG, PROJECTS, SERVICES, CONTACT } from "../data/site";

const PROOF = [
  { k: "Sedan", v: "2015" },
  { k: "Uppdrag", v: "Privat & företag" },
  { k: "Arbetsområde", v: "Skåne & Göteborg" },
  { k: "Specialitet", v: "Golvläggning" },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-graphite text-warmwhite" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.floor} alt="Nylagt trägolv med naturligt ljus – golvläggning av Casagon i Skåne och Göteborg" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-graphite via-graphite/55 to-graphite/30" />
      <div className="grain absolute inset-0" />

      <div className="u-container hero-container relative z-10 flex h-full flex-col justify-end pb-14 md:pb-20">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="overline text-wood"
        >
          <span className="mr-2 inline-block h-2 w-2 bg-casared align-middle" />
          Casagon Bygg & Allservice · Skåne & Göteborg
        </motion.p>

        <MaskText
          as="h1"
          animateOnLoad
          delay={0.35}
          lines={["Golvläggning.", "Från underarbete", "till färdigt golv."]}
          className="hero-title mt-5 font-display font-bold leading-[0.95] tracking-tight text-[8vw] sm:text-6xl lg:text-7xl"
        />

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
          className="hero-lead mt-8 max-w-xl text-base leading-relaxed text-warmwhite/70 md:text-lg"
        >
          Casagon hjälper privatpersoner och företag i hela Skåne och Göteborg med golvläggning.
          Vi hjälper även till med bygg och renovering när projektet kräver mer.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
          className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <Link
            to="/kontakt"
            data-testid="hero-quote-cta"
            className="group inline-flex items-center justify-center gap-3 bg-casared px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-warmwhite transition-colors duration-300 hover:bg-warmwhite hover:text-graphite"
          >
            Begär offert
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
          <Link
            to="/projekt"
            data-testid="hero-projects-cta"
            className="inline-flex items-center justify-center gap-3 border border-warmwhite/30 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-warmwhite transition-colors duration-300 hover:border-warmwhite"
          >
            Se utförda arbeten
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-2 text-xs uppercase tracking-[0.2em] text-warmwhite/50 md:flex"
      >
        <ArrowDown size={14} className="animate-bounce" /> Scrolla
      </motion.div>
    </section>
  );
}

function ProofStrip() {
  return (
    <section className="border-b border-graphite/10 bg-warmwhite" data-testid="proof-strip">
      <div className="u-container grid grid-cols-2 md:grid-cols-4">
        {PROOF.map((p, i) => (
          <Reveal
            key={p.k}
            delay={i * 0.08}
            className={`border-graphite/10 py-8 md:py-10 ${i % 2 === 0 ? "border-r pr-4" : "pl-4"} ${
              i < 2 ? "border-b md:border-b-0" : ""
            } ${i > 0 ? "md:border-l md:pl-8" : ""}`}
          >
            <p className="overline text-graphite/40">{p.k}</p>
            <p className="mt-2 font-display text-xl font-bold tracking-tight md:text-3xl">{p.v}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FloorSpecialty() {
  return (
    <section className="bg-warmwhite py-16 md:py-24" data-testid="floor-specialty">
      <div className="u-container grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="overline text-wood">
              <span className="mr-2 inline-block h-2 w-2 bg-casared align-middle" />
              Golvläggning
            </p>
          </Reveal>
          <MaskText
            as="h2"
            lines={["Rätt gjort", "från grunden."]}
            className="mt-6 font-display text-4xl font-bold leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl"
          />
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-graphite/70 md:text-lg">
              Casagon har lång erfarenhet av parkett och trägolv. Vi tar hand om arbetet från
              förberedelse och underlag till läggning och färdig finish.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <Link
              to="/golvlaggning"
              data-testid="floor-specialty-cta"
              className="link-underline mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-graphite"
            >
              Läs mer om golvläggning <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <div className="grid grid-cols-5 gap-4">
            <ParallaxImage
              src={IMG.floor}
              alt="Detalj av lagt trägolv"
              amount={40}
              className="col-span-3 aspect-[3/4]"
            />
            <div className="col-span-2 flex flex-col gap-4">
              <ParallaxImage src={IMG.staircase} alt="Trappa i trä" amount={60} className="aspect-[3/4]" />
              <div className="flex flex-1 flex-col justify-center border border-graphite/15 p-5">
                <p className="font-display text-4xl font-bold leading-none tracking-tight text-casared">10+</p>
                <p className="mt-2 text-xs uppercase tracking-[0.16em] text-graphite/50">års erfarenhet</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceArea() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-graphite py-20 text-warmwhite md:py-28" data-testid="service-area">
      <div className="u-container relative z-10">
        <Reveal>
          <p className="overline text-casared">Arbetsområde</p>
        </Reveal>

        <MaskText
          as="h2"
          lines={["Skåne & Göteborg."]}
          className="mt-6 max-w-[18ch] font-display text-4xl font-bold leading-[0.95] tracking-tight sm:text-5xl lg:text-7xl"
        />

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="overflow-hidden lg:col-span-8">
            <motion.img
              style={{ y }}
              src={IMG.exterior}
              alt="Utfört byggarbete i trä – Casagon tar uppdrag i Skåne och Göteborg"
              loading="lazy"
              className="aspect-[16/10] w-full scale-110 object-cover"
            />
          </div>
          <div className="flex flex-col justify-end lg:col-span-4">
            <Reveal>
              <p className="overline text-wood">Höör · Skåne · Göteborg</p>
              <p className="mt-5 text-base leading-relaxed text-warmwhite/70">
                Casagon utgår från Höör och tar uppdrag i Skåne och Göteborg. Vi hjälper både
                privatpersoner och företag, med golvläggning som tydlig specialitet.
              </p>
              <Link
                to="/kontakt"
                data-testid="service-area-cta"
                className="link-underline mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-warmwhite"
              >
                Begär offert <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function MoreThanFlooring() {
  const [active, setActive] = useState(0);
  return (
    <section className="bg-paper py-16 md:py-24" data-testid="more-than-flooring">
      <div className="u-container">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Reveal>
              <p className="overline text-wood">Bygg & renovering</p>
            </Reveal>
            <MaskText
              as="h2"
              lines={["När jobbet omfattar", "mer än golvet."]}
              className="mt-5 font-display text-4xl font-bold leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl"
            />
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-base leading-relaxed text-graphite/70">
              Casagon hjälper även till med kök, renovering, dörrar och fönster samt till- och
              ombyggnation.
            </p>
          </Reveal>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {SERVICES.map((s, i) => (
              <div
                key={s.n}
                data-testid={`service-row-${i}`}
                onMouseEnter={() => setActive(i)}
                className={`hairline group flex cursor-default items-center justify-between py-6 transition-colors md:py-8 ${
                  active === i ? "text-graphite" : "text-graphite/45"
                }`}
              >
                <div className="flex items-baseline gap-5 md:gap-8">
                  <span className="font-display text-sm font-bold text-casared">{s.n}</span>
                  <h3 className="font-display text-2xl font-bold tracking-tight md:text-4xl">{s.title}</h3>
                </div>
                <p className="hidden max-w-xs text-sm leading-snug text-graphite/60 md:block">{s.desc}</p>
              </div>
            ))}
            <div className="hairline" />
          </div>

          <div className="relative lg:col-span-5">
            <div className="sticky top-28 aspect-[4/5] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={SERVICES[active].img}
                  alt={SERVICES[active].title}
                  loading="lazy"
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="h-full w-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute bottom-0 left-0 bg-graphite px-5 py-3">
                <p className="text-xs uppercase tracking-[0.16em] text-warmwhite">{SERVICES[active].title}</p>
              </div>
            </div>
          </div>
        </div>

        <Reveal delay={0.1} className="mt-12">
          <Link
            to="/bygg-renovering"
            data-testid="more-services-cta"
            className="link-underline inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-graphite"
          >
            Utforska bygg & renovering <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ProjectPreview() {
  const items = [PROJECTS[1], PROJECTS[3], PROJECTS[5], PROJECTS[6]];
  return (
    <section className="bg-warmwhite py-16 md:py-24" data-testid="project-preview">
      <div className="u-container">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <MaskText
            as="h2"
            lines={["Utförda projekt"]}
            className="font-display text-4xl font-bold leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl"
          />
          <Reveal>
            <Link
              to="/projekt"
              data-testid="preview-all-cta"
              className="link-underline inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-graphite"
            >
              Se alla projekt <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-12">
          <ProjectFrame project={items[0]} className="md:col-span-7 aspect-[16/11]" big />
          <ProjectFrame project={items[1]} className="md:col-span-5 aspect-[16/11]" />
          <ProjectFrame project={items[2]} className="md:col-span-6 aspect-[16/9]" />
          <ProjectFrame project={items[3]} className="md:col-span-6 aspect-[16/9]" />
        </div>
      </div>
    </section>
  );
}

export function ProjectFrame({ project, className = "", big = false }) {
  return (
    <Reveal className={className}>
      <div className="group relative h-full overflow-hidden bg-graphite">
        <img
          src={project.img}
          alt={project.alt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-graphite/80 via-transparent to-transparent opacity-70" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 md:p-6">
          <div>
            <p className="overline text-wood">{project.label}</p>
            <h3 className={`mt-1 font-display font-bold tracking-tight text-warmwhite ${big ? "text-2xl md:text-3xl" : "text-lg md:text-xl"}`}>
              {project.title}
            </h3>
            {project.location && <p className="mt-0.5 text-xs text-warmwhite/60">{project.location}</p>}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function TrustRot() {
  return (
    <section className="grain relative overflow-hidden bg-surface py-12 text-warmwhite md:py-16" data-testid="trust-rot">
      <div className="u-container relative z-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="overline text-wood">Förtroende</p>
          </Reveal>
          <MaskText
            as="h2"
            lines={["Byggt på erfarenhet", "sedan 2015."]}
            className="mt-5 font-display text-4xl font-bold leading-[0.95] tracking-tight sm:text-5xl lg:text-[3.5rem]"
          />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-warmwhite/70">
              Casagon Bygg & Allservice har varit etablerat sedan 2015 och hjälper både
              privatpersoner och företag med golvläggning, bygg och renovering.
            </p>
          </Reveal>
          <div className="mt-7 flex flex-wrap gap-3">
            {["Sedan 2015", "Privat & företag", "Golvläggning"].map((t) => (
              <Reveal key={t}>
                <span className="inline-block border border-warmwhite/20 px-5 py-3 text-sm font-medium">{t}</span>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal>
            <p className="overline text-warmwhite/40">Bra att veta · ROT-avdrag</p>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-4 text-base leading-relaxed text-warmwhite/70">
              Många renoverings- och byggarbeten hos privatpersoner kan omfattas av ROT-avdrag.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-3 text-sm leading-relaxed text-warmwhite/50">
              Villkor och belopp styrs av Skatteverkets regler. Fråga oss gärna vad som kan gälla
              för ditt projekt.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function OffertSection() {
  return (
    <section className="grain relative overflow-hidden bg-graphite py-16 text-warmwhite md:py-24" data-testid="home-offert">
      <div className="u-container relative z-10 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="overline text-casared">Begär offert</p>
          </Reveal>
          <MaskText
            as="h2"
            lines={["Berätta vad du", "behöver hjälp", "med."]}
            className="mt-6 font-display text-4xl font-bold leading-[0.92] tracking-tight sm:text-5xl"
          />
          <Reveal delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-warmwhite/60">
              Fyll i formuläret så återkommer vi. Vill du hellre prata direkt?
            </p>
            <a href={CONTACT.phoneHref} data-testid="offert-phone" className="link-underline mt-3 inline-block font-display text-2xl font-bold tracking-tight">
              {CONTACT.phoneDisplay}
            </a>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <QuoteForm dark />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Golvläggning i Skåne & Göteborg · Casagon Bygg"
        description="Casagon är specialist på golvläggning och tar uppdrag i hela Skåne och hela Göteborg. Även bygg och renovering för privatpersoner och företag."
        path="/"
      />
      <Hero />
      <ProofStrip />
      <FloorSpecialty />
      <ServiceArea />
      <MoreThanFlooring />
      <ProjectPreview />
      <TrustRot />
      <OffertSection />
    </>
  );
}
